/**
 * SplitButton — a shareable primary action with a caret that opens a small
 * menu of secondary actions (BON-203 manual-test R5). The primary button runs
 * the default action; the caret reveals alternates (e.g. "Create & add
 * another"). Two-tier: this primitive is generic; callers supply the label +
 * the option set. Outside-click / Escape close the menu (mirrors
 * RegionalEyebrowPicker). Styled to match the CreateDrawer footer CTA.
 */
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface SplitButtonOption {
  /** Stable id (React key + test hook). */
  id: string;
  label: string;
  onSelect: () => void;
}

export interface SplitButtonProps {
  /** Primary button label, e.g. "Create draft". */
  label: string;
  /** Primary action (the big button). */
  onClick: () => void;
  /** Secondary actions revealed by the caret. */
  options: SplitButtonOption[];
  disabled?: boolean;
  /** Accessible label for the caret toggle. Default "More actions". */
  menuLabel?: string;
}

// Buttons don't inherit font-family by default, so set it explicitly to match
// the CreateDrawer footer buttons (DM Sans, 13px / 700, sentence-case) — without
// this the split CTA falls back to the UA font and reads inconsistently.
const FONT_FAMILY = "'DM Sans', system-ui, sans-serif";

// The primary half and the caret half share one green pill (matching heights, a
// single hairline divider, outer corners rounded only) so the control reads as
// one split button, not two adjacent buttons.
const PRIMARY_STYLE = (disabled: boolean): CSSProperties => ({
  height: 32,
  minWidth: 132,
  padding: '0 16px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px 0 0 6px',
  border: '1px solid transparent',
  fontFamily: FONT_FAMILY,
  lineHeight: 1,
  background: 'var(--nb-primary)',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

const CARET_STYLE = (disabled: boolean): CSSProperties => ({
  height: 32,
  width: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0 6px 6px 0',
  border: '1px solid transparent',
  borderLeft: '1px solid rgba(255,255,255,.35)',
  background: 'var(--nb-primary)',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

// Right-aligned to the control and sized to its CONTENT (not pinned to the
// button width) so longer options like "Create & add another" get room to
// breathe — the menu may extend past the button's left edge (Tailwind
// button-group pattern). `minWidth: 100%` keeps it at least as wide as the
// control; a 2px gap + matching radius keep it visually tied to the button.
const MENU_STYLE: CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 2px)',
  right: 0,
  minWidth: '100%',
  width: 'max-content',
  padding: 6,
  background: 'var(--nb-bg)',
  border: '1px solid var(--nb-border-2)',
  borderRadius: 6,
  boxShadow: '0 8px 20px rgba(20,30,20,0.14)',
  zIndex: 60,
};

// Same family/size as the unified button (inherited DM Sans, 13px).
const MENU_ITEM_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '7px 10px',
  borderRadius: 4,
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontFamily: FONT_FAMILY,
  color: 'var(--nb-ink)',
  cursor: 'pointer',
};

function OptionsMenu({ options, onClose }: { options: SplitButtonOption[]; onClose: () => void }) {
  return (
    <div role="menu" style={MENU_STYLE}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="menuitem"
          onClick={() => {
            onClose();
            o.onSelect();
          }}
          style={MENU_ITEM_STYLE}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--nb-primary-soft)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SplitButton({
  label,
  onClick,
  options,
  disabled = false,
  menuLabel = 'More actions',
}: SplitButtonProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasMenu = options.length > 0;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" disabled={disabled} onClick={onClick} style={PRIMARY_STYLE(disabled)}>
        {label}
      </button>
      {hasMenu && (
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={menuLabel}
          onClick={() => setOpen((v) => !v)}
          style={CARET_STYLE(disabled)}
        >
          <ChevronDown size={14} aria-hidden />
        </button>
      )}
      {open && hasMenu && <OptionsMenu options={options} onClose={() => setOpen(false)} />}
    </div>
  );
}
